<template>
    <div>
        <form-wrapper @submit="handleFormSubmission(data)">
            <form-row>
                <common-page-header
                    :heading="$t('pages.profile.professional.heading.verification')"
                    size="page-plain-header"
                ></common-page-header>
            </form-row>
            <form-row v-if="npiLabelData.text || licenseLabelData.text">
                <form-cell width="half">
                    <div v-if="npiLabelData.text" class="npi-badge npi-badge--npi-verified">
                        <common-icon name="check-circle" size="medium"></common-icon>
                        <span class="npi-badge__text">
                            {{ npiLabelData.text }}
                        </span>
                    </div>
                </form-cell>
                <form-cell width="half">
                    <div v-if="licenseLabelData.text" :class="getBadgeClass">
                        <common-icon v-if="licenseLabelData.icon" :name="licenseLabelData.icon" size="medium"></common-icon>
                        <span class="npi-badge__text">
                            {{ licenseLabelData.text }}
                        </span>
                    </div>
                </form-cell>
            </form-row>
            <form-row>
                <form-cell width="half">
                    <div class="npi-field-container">
                        <text-field
                            class="npi-field"
                            :label="$t('forms.auth.profile.fields.npi_number.label')"
                            :placeholder="$t('forms.auth.profile.fields.npi_number.placeholder')"
                            :isRequired="false"
                            :formData="data"
                            :error="npiFieldError"
                            valuePath="npiNumber"
                            :isDisabled="data.isNpiVerified"
                            @change="change($event)"
                        >
                            <template #error="{ error }">
                                {{ error }}
                            </template>
                        </text-field>
                        <span v-if="data.isNpiVerified" class="npi-verified-text"></span>
                        <template v-else>
                            <common-button
                                class="npi-verify-button"
                                :isProcessing="isVerifyingNpi"
                                :isDisabled="(data.npiNumber && data.isNpiVerified) || isVerifyingNpi"
                                :label="$t('forms.auth.profile.fields.npi_number.verify')"
                                type="secondary"
                                size="small"
                                @click.prevent="verifyNpi"
                            ></common-button>
                        </template>
                    </div>
                </form-cell>
                <form-cell width="half">
                    <template v-if="data.isLicenseVerified === true || data.isLicenseVerified === false">
                        <div class="license-upload-section">
                            <span>
                                {{ $t('forms.auth.profile.fields.license_file.label') }}
                                <span class="license-sub-text">
                                    ({{ $t('forms.auth.profile.fields.license_file.update_license_message') }})
                                </span>
                            </span>
                            <common-button
                                class="license-upload-btn"
                                :isProcessing="isUploadingLicense"
                                :label="$t('forms.auth.profile.fields.license_file.update_license')"
                                type="secondary"
                                size="small"
                                :isDisabled="false"
                                @click.prevent="uploadLicense"
                            ></common-button>
                            <common-field-error :error="licenseFieldError"></common-field-error>
                        </div>
                    </template>
                    <span v-else-if="data.licenseFileName && !data.isLicenseVerified"></span>
                    <template v-else>
                        <div class="license-upload-section">
                            <span>
                                {{ $t('forms.auth.profile.fields.license_file.label') }}
                                <span class="license-sub-text">
                                    ({{ $t('forms.auth.profile.fields.license_file.upload_license_message') }})
                                </span>
                            </span>
                            <common-button
                                class="license-upload-btn"
                                :isProcessing="isUploadingLicense"
                                :label="$t('forms.auth.profile.fields.license_file.upload_license')"
                                type="secondary"
                                size="small"
                                :isDisabled="false"
                                @click.prevent="uploadLicense"
                            ></common-button>
                            <common-field-error :error="licenseFieldError"></common-field-error>
                        </div>
                    </template>
                </form-cell>
            </form-row>

            <common-divider class="verification-divider"></common-divider>

            <form-row>
                <common-page-header
                    :heading="$t('pages.profile.professional.heading.specialities')"
                    size="page-plain-header"
                ></common-page-header>
            </form-row>

            <form-row align="bottom">
                <form-cell width="half">
                    <form-info-field :text="specialityName" :showDeleteButton="false"></form-info-field>
                </form-cell>
                <form-cell width="half">
                    <select-field
                        :label="$t('forms.auth.register.fields.physicianGroup.label')"
                        :placeholder="$t('forms.auth.register.fields.physicianGroup.placeholder')"
                        :options="physicianGroupsExtended"
                        :isRequired="false"
                        :formData="data"
                        valuePath="physicianGroupId"
                        @change="change($event)"
                    ></select-field>
                </form-cell>
            </form-row>

            <form-row>
                <form-cell>
                    <multi-select-field
                        :class="invalidSpecialitiesInputClass"
                        :label="$t('forms.professional_info.sub_specialities.label')"
                        :isRequired="true"
                        :formData="data"
                        :options="getSubSpeciality"
                        :oneOptionSelected="true"
                        valuePath="specialities"
                        @change="change($event)"
                    ></multi-select-field>
                </form-cell>
                <form-cell>
                    <year-field
                        :class="invalidPracticingSinceInputClass"
                        :label="$t('forms.auth.referrals.fields.practicing_since.label')"
                        :placeholder="$t('forms.auth.referrals.fields.practicing_since.placeholder')"
                        :isRequired="true"
                        :formData="data"
                        valuePath="practicingSince"
                        @change="change($event)"
                    ></year-field>
                </form-cell>
            </form-row>

            <form-row>
                <common-page-header
                    :heading="$t('pages.profile.professional.heading.licences')"
                    size="page-plain-header"
                ></common-page-header>
            </form-row>
            <licenses-fragment
                :states="states"
                :formData="data"
                :showHint="false"
                format="compact-non-mandatory"
                valuePath="medicalLicenses"
                @change="change($event)"
            ></licenses-fragment>

            <form-row align="bottom">
                <div class="form-row-cell">
                    <form-cell>
                        <number-field
                            class="form-field__fix_length"
                            :label="$t('forms.auth.referrals.fields.disciplinary_actions.label')"
                            :isRequired="true"
                            :formData="data"
                            valuePath="disciplinaryActions"
                            @change="change($event)"
                        ></number-field>
                    </form-cell>
                    <form-cell>
                        <number-field
                            class="form-field__fix_length"
                            :label="$t('forms.auth.referrals.fields.malpractice_claims_lost.label')"
                            :isRequired="true"
                            :formData="data"
                            :hint="$t('forms.auth.referrals.fields.malpractice_claims_lost.hint')"
                            valuePath="malpracticeClaimsLost"
                            @change="change($event)"
                        ></number-field>
                    </form-cell>
                    <form-cell>
                        <boolean-field
                            :label="$t('forms.auth.referrals.fields.board_certified.label')"
                            :isRequired="true"
                            :formData="data"
                            valuePath="boardCertified"
                            @change="change($event)"
                        ></boolean-field>
                    </form-cell>
                </div>
            </form-row>

            <form-row>
                <common-page-header
                    :heading="$t('pages.profile.professional.heading.short_biography')"
                    size="page-plain-header"
                ></common-page-header>
            </form-row>
            <form-row>
                <form-cell>
                    <textarea-field
                        :label="$t('forms.professional_info.short_biography.label')"
                        :placeholder="$t('forms.professional_info.short_biography.placeholder')"
                        :isRequired="false"
                        :formData="data"
                        valuePath="shortBiography"
                        @change="change($event)"
                    ></textarea-field>
                </form-cell>
            </form-row>

            <form-row>
                <common-page-header
                    :heading="$t('pages.profile.professional.heading.medical_schools')"
                    size="page-plain-header"
                    contentSpacing="medium"
                ></common-page-header>
            </form-row>
            <professional-info-fragment
                :states="states"
                :formData="data"
                :showRemoveProfessionalInfoModal="true"
                propertyName="medical school"
                translationPropertyName="medical_schools"
                valuePath="medicalSchools"
                @change="change($event)"
            ></professional-info-fragment>

            <form-row>
                <common-page-header
                    :heading="$t('pages.profile.professional.heading.affiliated_hospitals')"
                    size="page-plain-header"
                    contentSpacing="medium"
                ></common-page-header>
            </form-row>
            <professional-info-fragment
                :states="states"
                :formData="data"
                :showRemoveProfessionalInfoModal="true"
                propertyName="affiliated hospital"
                translationPropertyName="affiliated_hospitals"
                valuePath="affiliatedHospitals"
                @change="change($event)"
            ></professional-info-fragment>

            <form-row>
                <common-page-header
                    :heading="$t('pages.profile.professional.heading.residencies')"
                    size="page-plain-header"
                    contentSpacing="medium"
                ></common-page-header>
            </form-row>
            <professional-info-fragment
                :states="states"
                :formData="data"
                :showRemoveProfessionalInfoModal="true"
                propertyName="residency"
                translationPropertyName="residencies"
                valuePath="residencies"
                @change="change($event)"
            ></professional-info-fragment>

            <form-row>
                <common-page-header
                    :heading="$t('pages.profile.professional.heading.fellowships')"
                    size="page-plain-header"
                    contentSpacing="medium"
                ></common-page-header>
            </form-row>
            <professional-info-fragment
                :states="states"
                :formData="data"
                :showRemoveProfessionalInfoModal="true"
                propertyName="fellowship"
                translationPropertyName="fellowships"
                valuePath="fellowships"
                @change="change($event)"
            ></professional-info-fragment>

            <template #footer>
                <common-buttons>
                    <common-button
                        :isSubmittable="false"
                        :label="$t('pages.profile.professional.buttons.secondary')"
                        type="secondary"
                        size="large"
                        @click="handleCloseEvent()"
                    ></common-button>
                    <common-button
                        :isProcessing="isSending"
                        :isDisabled="isSubmitButtonDisabled()"
                        :label="$t('pages.profile.professional.buttons.primary')"
                        size="large"
                        type="primary"
                    ></common-button>
                </common-buttons>
            </template>
        </form-wrapper>
        <common-notification ref="commonSuccessNotificationChild" :content="notificationMessage"></common-notification>
    </div>
</template>

<script>
    import UsersAPI from 'api/UsersAPI'
    import FormMixin from 'mixins/FormMixin'
    import HandleRequestMixin from 'mixins/HandleRequestMixin'

    export default {
        mixins: [FormMixin, HandleRequestMixin],

        props: {
            states: { type: Array, required: true },
            specialities: { type: Array, required: true },
            physicianGroups: { type: Array, required: true },
        },

        data() {
            return {
                userId: null,
                specialityName: '',
                isVerifyingNpi: false,
                notificationMessage: '',
                npiFieldError: '',
                licenseFieldError: '',
                isUploadingLicense: false,
                licenseFileName: null,
            }
        },

        computed: {
            invalidNpiInputClass() {
                return { 'form-input-text--is-invalid': !this.data.npiNumber }
            },

            invalidSpecialitiesInputClass() {
                return { 'form-input-select--is-invalid': !this.data.specialities || this.data.specialities.length === 0 }
            },

            getSubSpeciality() {
                return this.specialities?.filter(
                    (item) => item.value === this.$auth.getActiveMember().specialities[0].speciality.id,
                )
            },

            invalidPracticingSinceInputClass() {
                return { 'form-input-select--is-invalid': !this.data.practicingSince }
            },

            specialityLabel() {
                const subspecialityId = this.data.specialities[0]
                const speciality = this.specialities.find((speciality) => {
                    return (
                        speciality.options && speciality.options.find((subspeciality) => subspeciality.value === subspecialityId)
                    )
                })
                if (speciality && speciality.label) {
                    return speciality.label
                }
                return ''
            },

            physicianGroupsExtended() {
                return [{ value: -1, label: 'None' }, ...this.physicianGroups]
            },

            isOriginalNpi() {
                const original = this.$auth.getActiveMember()
                return this.data.npiNumber === original.npiNumber
            },

            npiLabelData() {
                if (this.data.isNpiVerified) {
                    return {
                        text: this.$t('forms.auth.profile.fields.npi_number.npi_verified'),
                        badgeClass: 'npi-badge--npi-verified',
                        icon: 'check',
                    }
                } else {
                    return {
                        text: '',
                        badgeClass: '',
                        icon: 'check',
                    }
                }
            },
            licenseLabelData() {
                if (this.data.isLicenseVerified === true) {
                    return {
                        text: this.$t('forms.auth.profile.fields.license_file.license_verified'),
                        badgeClass: 'npi-badge--license-verified',
                        icon: 'check-circle',
                    }
                } else if (this.data.isLicenseVerified === false) {
                    return {
                        text: this.$t('forms.auth.profile.fields.license_file.license_rejected'),
                        badgeClass: 'npi-badge--license-rejected',
                        icon: null,
                    }
                } else if (this.data.licenseFileName) {
                    return {
                        text: this.$t('forms.auth.profile.fields.license_file.license_processing'),
                        badgeClass: 'npi-badge--processing',
                        icon: null,
                    }
                } else {
                    return {
                        text: '',
                        badgeClass: '',
                        icon: null,
                    }
                }
            },

            getBadgeClass() {
                return {
                    'npi-badge': true,
                    [this.licenseLabelData.badgeClass]: true,
                }
            },
        },
        methods: {
            setLicenseError(message = '') {
                this.licenseFieldError = message || ''
            },
            isSubmitButtonDisabled() {
                // Check if required fields are present
                const hasRequiredFields = this.data.specialities && this.data.specialities.length > 0 && this.data.practicingSince

                return !hasRequiredFields
            },

            handleFormSubmission(data) {
                const selectedSpecialities = this.getSpecialitiesDataForSubmission(data.specialities)
                const {
                    disciplinaryActions,
                    malpracticeClaimsLost,
                    boardCertified,
                    practicingSince,
                    shortBiography,
                    physicianGroupId,
                } = data

                const dataToSubmit = {
                    disciplinaryActions,
                    malpracticeClaimsLost,
                    boardCertified,
                    practicingSince,
                    shortBiography,
                    physicianGroupId: physicianGroupId !== -1 ? physicianGroupId : null,
                    medicalLicenses: this.getArrayDataForSubmission(data.medicalLicenses),
                    medicalSchools: this.getArrayDataForSubmission(data.medicalSchools),
                    affiliatedHospitals: this.getArrayDataForSubmission(data.affiliatedHospitals),
                    residencies: this.getArrayDataForSubmission(data.residencies),
                    fellowships: this.getArrayDataForSubmission(data.fellowships),
                    userSpecialities: selectedSpecialities,
                }

                this.updateUser(this.userId, dataToSubmit)
            },

            updateUser(userId, data) {
                this.handleRequest(UsersAPI.update(userId, data))
                    .then(() => {
                        this.$router.push({ name: 'index.home' })
                    })
                    .catch(() => {})
            },

            reloadData() {
                const physician = this.$auth.getActiveMember()

                this.userId = physician.id
                this.specialityName = physician.specialities[0].speciality.name

                const specialitiesSelection = physician.specialities.reduce((acc, value) => {
                    if (!acc.includes(value.subSpeciality.id)) {
                        acc.push(value.subSpeciality.id)
                    }
                    return acc
                }, [])

                const { shortBiography, physicianGroupId } = physician

                const dataToPrefill = {
                    npiNumber: physician.npiNumber,
                    isNpiVerified: physician.isNpiVerified,
                    licenseFileName: physician.licenseFileName,
                    isLicenseVerified: physician.isLicenseVerified,
                    physicianGroupId: physicianGroupId ? physicianGroupId : -1,
                    medicalSchools: this.getArrayDataToPrefill(physician.medicalSchools),
                    affiliatedHospitals: this.getArrayDataToPrefill(physician.affiliatedHospitals),
                    residencies: this.getArrayDataToPrefill(physician.residencies),
                    fellowships: this.getArrayDataToPrefill(physician.fellowships),
                    shortBiography,
                    medicalLicenses: this.getArrayDataToPrefill(physician.medicalLicenses),
                    specialities: specialitiesSelection,
                    practicingSince: physician.specialities[0].practicingSince,
                    disciplinaryActions: physician.disciplinaryActions || 0,
                    malpracticeClaimsLost: physician.malpracticeClaimsLost || 0,
                    boardCertified: physician.boardCertified || false,
                }
                this.prefillData(dataToPrefill)
            },

            handleCloseEvent() {
                this.$router.push({ name: 'index.home' })
            },

            getArrayDataForSubmission(array) {
                if (array && array.length) {
                    return array.filter((value) => Object.keys(value).length !== 0)
                }
                return []
            },

            getArrayDataToPrefill(array) {
                if (!array || !array.length) {
                    return [{}]
                }
                return array
            },

            getSpecialitiesDataForSubmission(specialitiesData) {
                return this.specialities
                    .flatMap((spec) => {
                        if (spec.options.length === 0) {
                            return spec
                        } else {
                            return spec.options
                        }
                    })
                    .filter((spec) => {
                        if (spec.data) {
                            return specialitiesData.includes(spec.data.id)
                        }
                        return specialitiesData.includes(spec.value)
                    })
                    .reduce((acc, speciality) => {
                        let spec
                        if (speciality.data) {
                            spec = {
                                specialityId: speciality.data.optionId,
                                subSpecialityId: speciality.value,
                            }
                        } else {
                            spec = {
                                specialityId: speciality.value,
                            }
                        }
                        acc.push(spec)
                        return acc
                    }, [])
            },

            async verifyNpi(event) {
                if (event) event.preventDefault && event.preventDefault()
                if (!this.data.npiNumber) return
                this.isVerifyingNpi = true
                try {
                    const { id: userId } = this.$auth.getActiveMember()
                    const { verified } = await UsersAPI.verifyNpi(userId, this.data.npiNumber)
                    if (verified) {
                        this.data.isNpiVerified = true
                        this.data.licenseFileName = null
                        this.npiFieldError = ''
                        this.notificationMessage = this.$t('forms.auth.profile.fields.npi_number.verification_success')
                        this.$refs.commonSuccessNotificationChild.showNotification()
                    } else {
                        this.data.isNpiVerified = false
                        this.npiFieldError = this.$t('forms.auth.profile.fields.npi_number.details_not_match')
                    }
                } catch (error) {
                    this.data.isNpiVerified = false
                    this.npiFieldError =
                        error?.response?.data?.[0]?.message ||
                        error?.response?.data?.message ||
                        this.$t('forms.auth.profile.fields.npi_number.verification_failed')
                } finally {
                    this.isVerifyingNpi = false
                }
            },

            uploadLicense(event) {
                this.notificationMessage = ''
                this.npiFieldError = ''
                if (event) event.preventDefault && event.preventDefault()

                const fileInput = document.createElement('input')
                fileInput.type = 'file'
                fileInput.accept = '.pdf,.doc,.docx,.jpg,.jpeg,.png'
                fileInput.style.display = 'none'

                fileInput.onchange = async (e) => {
                    const file = e.target.files[0]
                    if (!file) return

                    this.isUploadingLicense = true
                    try {
                        const { id: userId } = this.$auth.getActiveMember()
                        const response = await UsersAPI.uploadLicense(userId, file)
                        this.data.licenseFileName =
                            response.data?.data?.fileName || response.data?.fileName || response?.fileName || null
                        this.data.isLicenseVerified = null
                        this.notificationMessage = this.$t('forms.auth.profile.fields.license_file.upload_success')
                        this.$refs.commonSuccessNotificationChild.showNotification()
                    } catch (error) {
                        this.licenseFieldError =
                            error?.response?.data?.[0]?.message ||
                            error?.response?.data?.message ||
                            this.$t('forms.auth.profile.fields.license_file.upload_failed')
                    } finally {
                        this.isUploadingLicense = false
                    }
                }

                document.body.appendChild(fileInput)
                fileInput.click()
                document.body.removeChild(fileInput)
            },
        },

        watch: {
            'data.npiNumber'(newNpi) {
                const original = this.$auth.getActiveMember()
                this.data.isNpiVerified = newNpi === original.npiNumber ? original.isNpiVerified : false
                this.data.licenseFileName = newNpi === original.npiNumber ? original.licenseFileName : null
                this.npiFieldError = ''
            },
        },

        mounted() {
            this.reloadData()
            window.scrollTo(0, 0)
        },
    }
</script>
<style scoped lang="scss">
    @import 'styles/setup/_variables.scss';
    @import 'styles/setup/_mixins.scss';
    .form-row-cell {
        @include breakpoint(smartphone) {
            flex-wrap: wrap;
        }

        display: flex;
        justify-content: space-between;
        width: 100%;
        align-items: flex-end;
        max-width: 37rem;
    }
    .npi-verify-container {
        display: flex;
        align-items: center;
        gap: 12px;
    }
    .npi-field-container {
        display: flex;
        align-items: flex-start;
        gap: 12px;
    }

    .npi-field {
        margin-bottom: 0;
        width: 100%;
    }

    .npi-verified-text {
        margin-top: 2.1rem;
        margin-right: 12px;
        color: #28a745;
        font-weight: bold;
        display: inline-block;
    }

    .npi-verify-button {
        margin-top: 39px;
        margin-bottom: 0 !important;
        white-space: nowrap;
    }

    .npi-badge {
        margin-right: 12px;
        display: inline-flex;
        align-items: center;
        gap: 6px;
        border-radius: 16px;
        padding: 4px 10px;
        font-weight: bold;
        font-size: 12px;

        &__text {
            font-weight: bold;
            font-size: 12px;
        }

        svg {
            color: currentColor;
        }

        .common-icon {
            color: currentColor !important;
        }

        .npi-badge .common-icon {
            color: inherit !important;
        }

        .npi-badge .common-icon svg {
            color: inherit !important;
        }

        .npi-badge .common-icon svg path {
            fill: currentColor !important;
        }

        &--license-verified {
            background: #e8f5e8;
            border: 1px solid #00daa0;
            color: #00daa0;
        }

        &--license-rejected {
            background: #ffeaea;
            border: 1px solid #db0032;
            color: #db0032;
        }

        &--processing {
            background: #fff3e0;
            border: 1px solid #ff6d00;
            color: #ff6d00;
        }

        &--npi-verified {
            background: #e3f2fd;
            border: 1px solid #13b3e3;
            color: #13b3e3;
        }
    }

    .upload-license-message {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 12px 16px;
        background: #e3f2fd;
        border: 1px solid #13b3e3;
        border-radius: 8px;
        color: #13b3e3;
        font-size: 14px;
        font-weight: 500;
    }

    .license-upload-section {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
    }

    .license-sub-text {
        font-size: 12px;
        color: #666;
        font-style: italic;
    }
    .form-field__error p {
        font-size: 14px; // or any smaller size you prefer, e.g. 11px
        margin: 0;
    }
    .license-upload-btn {
        margin-top: 15px;
    }

    .verification-divider {
        margin: 3rem 0 !important;
    }
</style>
